import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartOptions, WidgetConfig, WidgetKind } from '@/types/widget';
import type {
  Aggregation,
  DateBucket,
  FilterExpression,
  QuerySpec,
} from '@/types/query';
import { aggregationLabelKey, buildSpec } from '@/engine/widget-spec';
import { KpiCard } from '@/widgets/KpiCard';
import { BarChartWidget } from '@/widgets/BarChart';
import { LineChartWidget } from '@/widgets/LineChart';
import { PieChartWidget } from '@/widgets/PieChart';
import { CompareTableWidget } from '@/widgets/CompareTableWidget';
import { useDatasetStore } from '@/store/dataset-store';
import { useUiStore } from '@/store/ui-store';
import { isDateType } from '@/features/filters/column-types';
import { DEFAULT_DATE_BUCKET } from '@/engine/widget-spec';

type WidgetProps = {
  widget: WidgetConfig;
  filters: FilterExpression[];
  onRemove: (id: string) => void;
};

/** Shared render-context derived in {@link Widget} and consumed by the
 *  per-kind renderers in {@link RENDERERS}. */
type RenderContext = {
  widget: WidgetConfig;
  spec: QuerySpec;
  handleRemove: () => void;
  handleSettings: () => void;
  options: ChartOptions | undefined;
  note: string | undefined;
  seriesNames: Record<string, string>;
  effectiveBucket: DateBucket | undefined;
};

/**
 * Per-kind render table — replaces the prior 5-branch if-chain
 * (R-19 from docs/audit/03-revision-targets.md). Each entry receives
 * the shared {@link RenderContext} and picks the fields its widget
 * implementation actually consumes; the exhaustive `Record` type
 * makes a missing entry a compile-time error if a new `WidgetKind`
 * ships without a renderer.
 */
const RENDERERS: Record<WidgetKind, (ctx: RenderContext) => JSX.Element> = {
  kpi: ({ widget, spec, handleRemove, handleSettings, note }) => (
    <KpiCard
      title={widget.title}
      spec={spec}
      onRemove={handleRemove}
      onSettings={handleSettings}
      note={note}
    />
  ),
  bar: ({
    widget,
    spec,
    handleRemove,
    handleSettings,
    options,
    note,
    seriesNames,
    effectiveBucket,
  }) => (
    <BarChartWidget
      widgetId={widget.id}
      title={widget.title}
      spec={spec}
      onRemove={handleRemove}
      onSettings={handleSettings}
      options={options}
      note={note}
      seriesNames={seriesNames}
      dateBucket={effectiveBucket}
      breakdownBy={widget.breakdownBy}
      breakdownLimit={widget.breakdownLimit}
      primaryLimit={widget.limit}
    />
  ),
  line: ({
    widget,
    spec,
    handleRemove,
    handleSettings,
    options,
    note,
    seriesNames,
    effectiveBucket,
  }) => (
    <LineChartWidget
      widgetId={widget.id}
      title={widget.title}
      spec={spec}
      onRemove={handleRemove}
      onSettings={handleSettings}
      options={options}
      note={note}
      seriesNames={seriesNames}
      dateBucket={effectiveBucket}
      breakdownBy={widget.breakdownBy}
      breakdownLimit={widget.breakdownLimit}
      primaryLimit={widget.limit}
    />
  ),
  pie: ({
    widget,
    spec,
    handleRemove,
    handleSettings,
    options,
    note,
    effectiveBucket,
  }) => (
    <PieChartWidget
      widgetId={widget.id}
      title={widget.title}
      spec={spec}
      onRemove={handleRemove}
      onSettings={handleSettings}
      options={options}
      note={note}
      dateBucket={effectiveBucket}
    />
  ),
  'compare-table': ({ widget, spec, handleRemove, handleSettings, note }) => (
    <CompareTableWidget
      widgetId={widget.id}
      title={widget.title}
      spec={spec}
      {...(widget.limit ? { limit: widget.limit } : {})}
      onRemove={handleRemove}
      onSettings={handleSettings}
      note={note}
    />
  ),
};

/**
 * Dispatch component: renders the right widget implementation for a
 * {@link WidgetConfig} and wires remove + settings handlers into its
 * title bar. The per-kind render is a table lookup; see
 * {@link RENDERERS}.
 */
export function Widget({ widget, filters, onRemove }: WidgetProps): JSX.Element {
  const { t } = useTranslation();
  const groupByColumnSqlType = useDatasetStore((s) => {
    if (!widget.groupBy) return undefined;
    const cols = s.datasets.get(widget.datasetId)?.columns;
    return cols?.find((c) => c.name === widget.groupBy)?.sqlType;
  });
  const valueGroupings = useDatasetStore(
    (s) => s.datasets.get(widget.datasetId)?.valueGroupings,
  );
  const spec = useMemo(
    () => buildSpec(widget, filters, groupByColumnSqlType, valueGroupings),
    [widget, filters, groupByColumnSqlType, valueGroupings],
  );
  const setWidgetSettingsTarget = useUiStore((s) => s.setWidgetSettingsTarget);

  const handleRemove = useMemo(
    () => (): void => onRemove(widget.id),
    [widget.id, onRemove],
  );
  const handleSettings = (): void => setWidgetSettingsTarget(widget.id);

  // Build series name map from metric aliases → column labels. Prefers
  // the user-edited `label` when present, otherwise falls back to the
  // localized `${agg} ${col}` template (re-runs on locale switch via
  // the `t` dep).
  const seriesNames = useMemo(() => {
    const defaultLabel = (m: {
      aggregation: Aggregation;
      column: string;
      label?: string;
    }): string =>
      m.label?.trim() ||
      `${t(aggregationLabelKey(m.aggregation))} ${m.column}`;
    const map: Record<string, string> = { metric: defaultLabel(widget.metric) };
    widget.extraMetrics?.forEach((em, i) => {
      map[`metric_${i + 1}`] = defaultLabel(em);
    });
    return map;
  }, [widget.metric, widget.extraMetrics, t]);

  // Effective date bucket — only relevant when the X axis is a DATE
  // column.
  const effectiveBucket =
    groupByColumnSqlType && isDateType(groupByColumnSqlType)
      ? (widget.dateBucket ?? DEFAULT_DATE_BUCKET)
      : undefined;

  return RENDERERS[widget.kind]({
    widget,
    spec,
    handleRemove,
    handleSettings,
    options: widget.chartOptions,
    note: widget.note,
    seriesNames,
    effectiveBucket,
  });
}
